import { useCallback, useEffect, useMemo, useState } from 'react'

import type { DashboardMetadata } from '@factorialco/f0-react'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Cross, Download } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { useDashboardCanvas } from './DashboardProvider'
import type { DashboardCanvasContent } from './types'

export type DashboardHeaderProps = {
  content: DashboardCanvasContent
  onClose: () => void
}

/**
 * Canvas header for the dashboard entity. Layout, title, status tag,
 * metadata strip, and the action row (export + close) are all delegated
 * to `ResourceHeader`. The close button is just another `secondaryAction`
 * with `hideLabel`, so the shared header component doesn't need to know
 * about canvas-specific close affordances.
 */
export function DashboardHeader({ content, onClose }: DashboardHeaderProps) {
  const { canvasActions, exportAsExcel } = useDashboardCanvas()
  const [isExporting, setIsExporting] = useState(false)
  const [metadata, setMetadata] = useState<DashboardMetadata | null>(null)

  const savedDashboardId = content.savedDashboardId
  const getMetadata = canvasActions?.dashboard?.getMetadata

  // Fetch creator + last-edited only once the dashboard has been persisted.
  // Clear immediately on id change so we never flash the previous dashboard's
  // metadata while the new request is in flight.
  useEffect(() => {
    setMetadata(null)

    if (!savedDashboardId || !getMetadata) {
      return
    }

    let isCurrent = true
    void (async () => {
      try {
        const result = await getMetadata(savedDashboardId)
        if (!isCurrent) return
        setMetadata(result ?? null)
      } catch (error) {
        if (!isCurrent) return
        // eslint-disable-next-line no-console
        console.warn('Failed to load dashboard metadata', {
          dashboardId: savedDashboardId,
          error,
        })
        setMetadata(null)
      }
    })()

    return () => {
      isCurrent = false
    }
  }, [savedDashboardId, getMetadata])

  const handleExport = useCallback(async () => {
    if (!exportAsExcel) return
    setIsExporting(true)
    try {
      await exportAsExcel()
    } finally {
      setIsExporting(false)
    }
  }, [exportAsExcel])

  const secondaryActions = useMemo(() => {
    const actions = []
    if (exportAsExcel) {
      actions.push({
        label: i18n.t('common.ai.data_download.export'),
        icon: Download,
        onClick: handleExport,
        loading: isExporting,
      })
    }
    actions.push({
      label: i18n.t('common.close'),
      icon: Cross,
      hideLabel: true,
      onClick: onClose,
    })
    return actions
  }, [exportAsExcel, isExporting, handleExport, onClose])

  const isSaved = Boolean(content.savedDashboardId && content.savedDashboardCategory)
  const isUnsaved = isSaved && content.savedDashboardUnsaved === true

  const statusLabel = i18n.t('common.ai.dashboard.status_label')
  const status = isUnsaved
    ? {
        label: statusLabel,
        text: i18n.t('common.ai.dashboard.status.unsaved'),
        variant: 'warning' as const,
      }
    : isSaved
      ? {
          label: statusLabel,
          text: i18n.t('common.ai.dashboard.status.saved'),
          variant: 'positive' as const,
        }
      : {
          label: statusLabel,
          text: i18n.t('common.ai.dashboard.status.draft'),
          variant: 'neutral' as const,
        }

  const headerMetadata = useMemo(() => {
    if (!metadata) return undefined

    const fullName = `${metadata.creator.firstName} ${metadata.creator.lastName}`.trim()

    const editedAt =
      metadata.lastEdited instanceof Date ? metadata.lastEdited : new Date(metadata.lastEdited)
    const hasValidDate = !Number.isNaN(editedAt.getTime())
    const formattedDate = hasValidDate
      ? editedAt.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : null

    return [
      {
        label: i18n.t('common.ai.dashboard.created_by'),
        value: {
          type: 'avatar' as const,
          variant: {
            type: 'person' as const,
            firstName: metadata.creator.firstName,
            lastName: metadata.creator.lastName,
            src: metadata.creator.src,
          },
          text: fullName,
        },
      },
      formattedDate
        ? {
            label: i18n.t('common.ai.dashboard.last_edited'),
            value: { type: 'date' as const, formattedDate },
          }
        : undefined,
    ]
  }, [metadata])

  const displayTitle = metadata?.title ?? content.title
  const displayDescription = metadata?.description ?? content.config.description

  return (
    <ResourceHeader
      title={displayTitle}
      description={displayDescription}
      status={status}
      metadata={headerMetadata}
      secondaryActions={secondaryActions}
    />
  )
}
